/**
 * Admin-authenticated web push (subscription bound to admin cookie).
 */
import {
  isPushSupported,
  getPushStatus,
} from './webPush';

const API = process.env.REACT_APP_BACKEND_URL;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function arrayBufferToBase64(buf) {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
  return window.btoa(bin);
}

export { isPushSupported, getPushStatus };

export async function enableAdminPush() {
  if (!isPushSupported()) {
    throw new Error('Your browser does not support push notifications.');
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission was denied.');
  }

  let reg = await navigator.serviceWorker.getRegistration('/');
  if (!reg) {
    reg = await navigator.serviceWorker.register('/service-worker.js');
  }
  await navigator.serviceWorker.ready;

  const keyRes = await fetch(`${API}/api/push/vapid-public-key`);
  if (!keyRes.ok) throw new Error('Failed to fetch VAPID public key');
  const { public_key } = await keyRes.json();

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(public_key),
    });
  }

  const res = await fetch(`${API}/api/admin/push/subscribe`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      endpoint: sub.endpoint,
      keys: {
        p256dh: arrayBufferToBase64(sub.getKey('p256dh')),
        auth: arrayBufferToBase64(sub.getKey('auth')),
      },
    }),
  });
  if (!res.ok) throw new Error('Admin subscribe failed');

  try {
    const { enablePushAllAlerts } = await import('./adminAlerts');
    await enablePushAllAlerts();
  } catch (_) {
    /* subscribe route also saves prefs; best-effort */
  }

  return sub;
}

export async function disableAdminPush() {
  if (!isPushSupported()) return false;
  const reg = await navigator.serviceWorker.getRegistration('/');
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return true;

  try {
    await fetch(`${API}/api/admin/push/unsubscribe`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint: sub.endpoint }),
    });
  } catch (_) {
    /* best-effort */
  }
  await sub.unsubscribe();
  return true;
}

export async function getAdminPushStatus() {
  const base = await getPushStatus();
  try {
    const res = await fetch(`${API}/api/admin/push/status`, { credentials: 'include' });
    if (res.ok) {
      const data = await res.json();
      return { ...base, serverSubscribed: data.subscribed, subscriptionCount: data.subscription_count };
    }
  } catch (_) {
    /* ignore */
  }
  return { ...base, serverSubscribed: false };
}
